/* Grey Continent Image Imports */
import BRContinentGrey from "@assets/images/regions/continents/grey/BR.svg";
import EUNEContinentGrey from "@assets/images/regions/continents/grey/EUNE.svg";
import EUWContinentGrey from "@assets/images/regions/continents/grey/EUW.svg";
import LANContinentGrey from "@assets/images/regions/continents/grey/LAN.svg";
import LASContinentGrey from "@assets/images/regions/continents/grey/LAS.svg";
import MEContinentGrey from "@assets/images/regions/continents/grey/ME.svg";
import NAContinentGrey from "@assets/images/regions/continents/grey/NA.svg";
import OCEContinentGrey from "@assets/images/regions/continents/grey/OCE.svg";
import RUContinentGrey from "@assets/images/regions/continents/grey/RU.svg";
import TRContinentGrey from "@assets/images/regions/continents/grey/TR.svg";

/* White Continent Image Imports */
import BRContinentWhite from "@assets/images/regions/continents/white/BR.png";
import EUNEContinentWhite from "@assets/images/regions/continents/white/EUNE.png";
import EUWContinentWhite from "@assets/images/regions/continents/white/EUW.png";
import LANContinentWhite from "@assets/images/regions/continents/white/LAN.png";
import LASContinentWhite from "@assets/images/regions/continents/white/LAS.png";
import MEContinentWhite from "@assets/images/regions/continents/white/ME.png";
import NAContinentWhite from "@assets/images/regions/continents/white/NA.png";
import OCEContinentWhite from "@assets/images/regions/continents/white/OCE.png";
import RUContinentWhite from "@assets/images/regions/continents/white/RU.png";
import TRContinentWhite from "@assets/images/regions/continents/white/TR.png";
import {
  type AppLolRegion,
  EAppLolRegions,
  EAppRegions,
  type IAppRegionInfo,
} from "@core-types/region.enum";

export default class LolRegionHelper {
  static DEFAULT_REGION: AppLolRegion = EAppLolRegions.EUW;

  static REGION_LABELS = {
    [EAppLolRegions.EUW]: "Europe West",
    [EAppLolRegions.NA]: "North America",
    [EAppLolRegions.EUNE]: "Europe Nordic & East",
    [EAppLolRegions.OCE]: "Oceania",
    [EAppLolRegions.BR]: "Brazil",
    [EAppLolRegions.RU]: "Russia",
    [EAppLolRegions.TR]: "Turkey",
    [EAppLolRegions.LAN]: "Latin America North",
    [EAppLolRegions.LAS]: "Latin America South",
    [EAppLolRegions.ME]: "Middle East",
  };

  private static GREY_CONTINENTS = {
    [EAppLolRegions.EUW]: EUWContinentGrey,
    [EAppLolRegions.NA]: NAContinentGrey,
    [EAppLolRegions.EUNE]: EUNEContinentGrey,
    [EAppLolRegions.OCE]: OCEContinentGrey,
    [EAppLolRegions.BR]: BRContinentGrey,
    [EAppLolRegions.RU]: RUContinentGrey,
    [EAppLolRegions.TR]: TRContinentGrey,
    [EAppLolRegions.LAN]: LANContinentGrey,
    [EAppLolRegions.LAS]: LASContinentGrey,
    [EAppLolRegions.ME]: MEContinentGrey,
  };

  private static WHITE_CONTINENTS = {
    [EAppLolRegions.EUW]: EUWContinentWhite,
    [EAppLolRegions.NA]: NAContinentWhite,
    [EAppLolRegions.EUNE]: EUNEContinentWhite,
    [EAppLolRegions.OCE]: OCEContinentWhite,
    [EAppLolRegions.BR]: BRContinentWhite,
    [EAppLolRegions.RU]: RUContinentWhite,
    [EAppLolRegions.TR]: TRContinentWhite,
    [EAppLolRegions.LAN]: LANContinentWhite,
    [EAppLolRegions.LAS]: LASContinentWhite,
    [EAppLolRegions.ME]: MEContinentWhite,
  };

  static DEFAULT_REGION_INFOS: IAppRegionInfo = {
    code: LolRegionHelper.DEFAULT_REGION,
    codeLabel: LolRegionHelper.DEFAULT_REGION.toUpperCase(),
    label: LolRegionHelper.REGION_LABELS[LolRegionHelper.DEFAULT_REGION],
    greyImage: LolRegionHelper.GREY_CONTINENTS[LolRegionHelper.DEFAULT_REGION],
    whiteImage:
      LolRegionHelper.WHITE_CONTINENTS[LolRegionHelper.DEFAULT_REGION],
  };

  static getRegionContinent = (
    regionCode: keyof typeof LolRegionHelper.REGION_LABELS,
  ): {
    grey: string;
    white: string;
  } => ({
    grey: LolRegionHelper.GREY_CONTINENTS[regionCode],
    white: LolRegionHelper.WHITE_CONTINENTS[regionCode],
  });

  static getRegionHref = (
    currentPath: string,
    targetRegionCode: AppLolRegion,
  ) => {
    // Use ALL regions to ensure we strip out any existing region, even if it's from another game
    const regionKeys = Object.values(EAppRegions);
    const categorySlug = [
      "normal",
      "ranked",
      "handlevel",
      "acheter-compte-valorant",
      "buy-valorant-account",
    ];
    const segments = currentPath.split("/").filter(Boolean);

    let finalPath = "";

    for (const segment of segments) {
      // Skip current region segment
      if (regionKeys.includes(segment as any)) {
        continue;
      }

      finalPath += `/${segment}`;

      // Insert target region after category slug
      if (categorySlug.includes(segment)) {
        finalPath += `/${targetRegionCode}`;
      }
    }

    return finalPath || "/";
  };

  static getAllInfo = (): IAppRegionInfo[] => {
    const regions: IAppRegionInfo[] = [];

    for (const regionCode in LolRegionHelper.REGION_LABELS) {
      regions.push({
        code: regionCode as AppLolRegion,
        codeLabel: regionCode.toUpperCase() as AppLolRegion,
        label:
          LolRegionHelper.REGION_LABELS[
            regionCode as keyof typeof LolRegionHelper.REGION_LABELS
          ],
        greyImage:
          LolRegionHelper.GREY_CONTINENTS[
            regionCode as keyof typeof LolRegionHelper.REGION_LABELS
          ],
        whiteImage:
          LolRegionHelper.WHITE_CONTINENTS[
            regionCode as keyof typeof LolRegionHelper.REGION_LABELS
          ],
      });
    }

    return regions;
  };

  static getInfoByCode = (code: AppLolRegion) => {
    const allRegions = LolRegionHelper.getAllInfo();

    const infos = allRegions.find((region) => region.code === code);

    return infos ?? LolRegionHelper.DEFAULT_REGION_INFOS;
  };

  static convertToRegionEnum = (region: string): AppLolRegion => {
    const regionKey = Object.keys(LolRegionHelper.REGION_LABELS).find(
      (key) => key.toLowerCase() === region.toLowerCase(),
    );

    return regionKey
      ? (regionKey as AppLolRegion)
      : LolRegionHelper.DEFAULT_REGION;
  };
}
